import { CURRENT_ONBOARDING_VERSION } from '@mondaygpt/constants'
import { motion, type Variants } from 'framer-motion'
import { updateCurrentUser } from 'modules/authentication'
import { useAppDispatch } from 'modules/core'
import { useTopicStore } from 'modules/creative/store/topic-store'
import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCliAuthCallback, useRedirectUrl, useToast } from 'hooks'
import { setOnboardingCompleted } from '../slices'

interface SegwayStepProps {
  /** Already-fired by Phase 3 confirm? If true we just finalize. */
  brandKitAlreadySaved: boolean
  saveBrandKit: () => Promise<void>
}

// Visible beat is `max(MIN_SEGWAY_MS, side_effects_complete)` capped at
// MAX_SEGWAY_MS. Tuned so the two-line reveal lands fully (~3.7s) AND the
// user has a beat to read the punchline before the auto-handoff fires.
const MIN_SEGWAY_MS = 5500
const MAX_SEGWAY_MS = 7500

/**
 * Curtain-pull between the paywall and the product. Owns the
 * post-paywall side-effect chain (saveBrandKit, suggestion generation
 * trigger, mark complete, CLI handoff, redirect) but holds the user on a
 * two-line reveal while it runs. (No autopilot is created here — that's an
 * explicit opt-in via the weekly-plan wizard on Home.)
 * Visible window is bounded by `MIN_SEGWAY_MS`/`MAX_SEGWAY_MS`.
 */
export const SegwayStep: React.FC<SegwayStepProps> = ({ brandKitAlreadySaved, saveBrandKit }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { addToast } = useToast()
  const { redirectPath, redirectUrlSearchParam } = useRedirectUrl()
  const { isCliMode, sendTokensToCli } = useCliAuthCallback()
  // The effect below re-runs whenever the parent re-renders (`saveBrandKit`
  // is an inline prop with fresh identity), and StrictMode remounts it in
  // dev. Mount time and the in-flight finalize live in refs so every re-run
  // re-arms the timers against the ORIGINAL deadline instead of restarting
  // or — worse — clearing them in cleanup and never re-arming.
  const startTimeRef = useRef<number | null>(null)
  const finalizePromiseRef = useRef<Promise<void> | null>(null)
  const handedOffRef = useRef(false)
  // Finalize success is tracked in a ref and the redux `onboardingCompleted`
  // flip is deferred to handoff: OnboardingGuard bounces completed users off
  // /onboarding, so flipping the flag mid-hold cuts the segway to a flash.
  const finalizedRef = useRef(false)

  // Mirror of `handoff` so the in-flight `run()` always sees the latest
  // closure (otherwise its captured handoff goes stale when downstream
  // hooks re-derive after `setOnboardingCompleted`, and the auto-timer
  // calls a dead-component navigate).
  const handoffRef = useRef<() => void>(() => {})

  // Called by the auto-timer only; double-fire-guarded.
  const handoff = useCallback(async () => {
    if (handedOffRef.current) return
    handedOffRef.current = true
    try {
      if (isCliMode) {
        const sent = await sendTokensToCli()
        if (sent) {
          if (finalizedRef.current) dispatch(setOnboardingCompleted(true))
          navigate('/cli-signup-success', { replace: true })
          return
        }
        // A dead CLI must not strand the user on a buttonless curtain (there
        // is no manual escape anymore): toast, then fall through to the web
        // handoff. The CLI can re-run its login; the account is fine.
        addToast(
          'error',
          'Failed to send credentials to CLI. The CLI process may have stopped.',
          8000
        )
      }
      // Strip pre-onboarding redirect targets that would loop the user back
      // here (e.g., a stale `redirectUrl=/login` or `/onboarding` query that
      // pre-dates auth completion). Only same-origin relative paths can be
      // navigated by react-router; external URLs and protocol-relative ones
      // (`//evil.example`) silently no-op, so coerce them to `/`.
      const target = redirectPath || redirectUrlSearchParam || '/'
      const isSameOriginPath = target.startsWith('/') && !target.startsWith('//')
      const isLoopTarget = target === '/login' || target.startsWith('/onboarding')
      const safe = !isSameOriginPath || isLoopTarget ? '/' : target
      // Flag flip and navigate must stay in the same synchronous frame: the
      // flip alone would let OnboardingGuard render a competing redirect.
      if (finalizedRef.current) dispatch(setOnboardingCompleted(true))
      // `replace` so the back button from /home (or wherever) doesn't return
      // to /onboarding — onboarding is terminal and the guard intentionally
      // re-renders it on direct hits, so leaving it in history loops the user.
      navigate(safe, { replace: true })
    } catch (err) {
      handedOffRef.current = false
      console.error('[SegwayStep] Handoff failed:', err)
      addToast('error', "Something didn't load. Refresh and we'll get you in.", 5000)
    }
  }, [
    isCliMode,
    sendTokensToCli,
    navigate,
    redirectPath,
    redirectUrlSearchParam,
    addToast,
    dispatch,
  ])

  handoffRef.current = handoff

  // True-unmount insurance: if the segway is torn down after the server write
  // landed but before handoff ran, flip the flag so the guard doesn't route
  // the user back into a flow the server considers finished. Empty deps →
  // cleanup fires only on real unmount (StrictMode's simulated unmount runs
  // at mount time, before finalize can have succeeded).
  useEffect(() => {
    return () => {
      if (finalizedRef.current && !handedOffRef.current) dispatch(setOnboardingCompleted(true))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (startTimeRef.current === null) startTimeRef.current = Date.now()
    const startTime = startTimeRef.current
    const remaining = (deadlineMs: number) => Math.max(0, deadlineMs - (Date.now() - startTime))

    const finalize = async () => {
      try {
        if (!brandKitAlreadySaved) {
          await saveBrandKit()
        }

        // Must run AFTER the brand-kit write — the server endpoint reads
        // the user's current brand kit; firing before save would generate
        // suggestions against the stale or empty kit.
        void useTopicStore.getState().fetchTopics()

        // NOTE: we intentionally do NOT create an autopilot plan here. An
        // autopilot schedules and publishes on the user's behalf, so it now
        // requires explicit consent — the user opts in via the weekly-plan
        // setup wizard on Home (WeeklyPlanWizard). That wizard's create call
        // carries `{ timezone }` so the first slot batch still fires at the
        // brand's wall clock.

        await dispatch(
          updateCurrentUser({
            onboardingData: {
              completedVersion: CURRENT_ONBOARDING_VERSION,
              currentVersion: undefined,
              flow: null,
              progress: null,
            },
          })
        ).unwrap()

        finalizedRef.current = true
        // If the hard cap already fired the handoff (slow finalize), the user
        // is past the segway with the flag still false — flip it now so the
        // guard stops bouncing them back into onboarding.
        if (handedOffRef.current) dispatch(setOnboardingCompleted(true))
      } catch (err) {
        console.error('[SegwayStep] Failed to finalize:', err)
        addToast(
          'error',
          "Something didn't save. Refresh and we'll pick up where you left off.",
          5000
        )
      }
    }

    // Side effects fire exactly once (first effect run wins); timers re-arm
    // on every run against the original mount's deadlines, so a mid-hold
    // effect re-run (or StrictMode remount) can't kill the hard cap.
    finalizePromiseRef.current ??= finalize()

    let hardCapTimer: ReturnType<typeof setTimeout> | undefined
    let holdTimer: ReturnType<typeof setTimeout> | undefined
    // Cancelled on cleanup so a superseded run can't fire a stale handoff
    // (or navigate from a dead component on real unmount).
    let cancelled = false

    const run = async () => {
      const hardCap = new Promise<void>(resolve => {
        hardCapTimer = setTimeout(resolve, remaining(MAX_SEGWAY_MS))
      })
      await Promise.race([finalizePromiseRef.current, hardCap])
      if (cancelled) return

      const holdMs = remaining(MIN_SEGWAY_MS)
      if (holdMs > 0) {
        await new Promise<void>(resolve => {
          holdTimer = setTimeout(resolve, holdMs)
        })
      }
      if (!cancelled) handoffRef.current()
    }

    run()

    return () => {
      cancelled = true
      if (hardCapTimer) clearTimeout(hardCapTimer)
      if (holdTimer) clearTimeout(holdTimer)
    }
  }, [brandKitAlreadySaved, saveBrandKit, dispatch, addToast])

  return (
    // Full-viewport takeover. This is the post-payment delight beat, so it
    // centers in the VIEWPORT — not in the shell's padded scroll column,
    // which pushed the reveal below the fold on tall screens. `fixed` also
    // takes it out of flow, so the shell can never grow a scrollbar for it.
    // z-30 sits above the shell chrome (logo/back are z-20): the logo is an
    // <a href="/"> whose full-page load would abort the in-flight finalize,
    // so nothing may be clickable while the curtain settles the account.
    <div className="fixed inset-0 z-30 flex flex-col items-center justify-center overflow-hidden text-center">
      <SegwaySpotlights />
      <SegwayHeadline />
    </div>
  )
}

const SegwaySpotlights: React.FC = () => (
  <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden>
    <span className="segway-spotlight segway-spotlight-1" />
    <span className="segway-spotlight segway-spotlight-2" />
    <span className="segway-spotlight segway-spotlight-3" />
  </div>
)

const SETUP_LINE = 'you used to struggle managing your business social...'
const PUNCHLINE = "We'll take it from here."
const PUNCHLINE_DELAY_S = 1.4

const setupVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.2, 0.8, 0.2, 1] } },
}

// No per-word stagger — the reveal as a single unit is what reads as
// "gentle". Blur-clear keeps the type from snapping into focus.
const punchlineVariants: Variants = {
  hidden: { opacity: 0, y: 6, filter: 'blur(8px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 1.6, delay: PUNCHLINE_DELAY_S, ease: [0.2, 0.8, 0.2, 1] },
  },
}

const SegwayHeadline: React.FC = () => (
  <div className="relative flex max-w-[820px] flex-col items-center gap-6 px-4">
    <motion.p
      variants={setupVariants}
      initial="hidden"
      animate="visible"
      className="text-[20px] leading-[1.4] text-paper-dim sm:text-[26px]"
    >
      {SETUP_LINE}
    </motion.p>
    <motion.h2
      variants={punchlineVariants}
      initial="hidden"
      animate="visible"
      className="text-[44px] leading-[1.05] font-semibold tracking-[-0.02em] text-paper sm:text-[60px]"
    >
      {PUNCHLINE}
    </motion.h2>
  </div>
)
